import { ButtonDS, RadioButton, Typography, useDevice } from "get-life-storybook-ts";
import React, { useContext, useEffect, useState } from "react";
import { GetlifeContext } from "../../../../../context/GetLifeContext.context";
import { StatusContext } from "../../../../../context/StatusContext";
import { I18nContext } from "../../../../../context/i18n.context";
import HttpBeneficiariesRepository from "../../../../../api/request/Beneficiaries/Beneficiaries.service";
import HttpClientConsentsRepository from "../../../../../api/request/Consents/ClientConsents.service";
import "../../../../Questions/css/questions.scss";
import "../../../../../components/Components/FormBuilder/formBuilder.scss";
import "../../../../../components/Components/forms/beneficiaries/formPhysicalPerson.scss";
import { trackSubmissionError } from "../../../../../helpers/Tracking";

// Alta de un comercial (caller) que el cliente cierra con su enlace: los
// consentimientos los da el cliente, no el comercial (D-C3 «Caller IT al día»).
// ms-agl pone `consents_pending` antes de la nota legal; al guardarlos el estado
// pasa a `legalnote_pending` y getStatus lleva a /payment/4. La pregunta de
// beneficiarios es la del paso de beneficiarios organic y solo sale si el
// comercial designó beneficiarios con nombre. Native es solo IT: copy en italiano.
export const CLIENT_CONSENTS_TITLE = "I tuoi consensi";
export const CLIENT_CONSENTS_BODY =
  "Prima della nota legale ti chiediamo due consensi. Li dai tu: il tuo consulente non può darli al posto tuo.";
export const CLIENT_CONSENTS_MARKETING =
  "Acconsenti a ricevere comunicazioni commerciali di Life5: promozioni, nuovi prodotti e consigli sulla tua protezione?";
export const CLIENT_CONSENTS_ERROR =
  "Non siamo riusciti a salvare i tuoi consensi. Riprova tra qualche istante.";

type Answer = "" | "yes" | "no";

const ANSWERS: Array<{ value: Exclude<Answer, "">; label: string }> = [
  { value: "yes", label: "Sì" },
  { value: "no", label: "No" },
];

const Question = ({
  name,
  text,
  value,
  onChange,
}: {
  name: string;
  text: string;
  value: Answer;
  onChange: (value: Answer) => void;
}) => (
  <div className="flex flex-col gap-4 w-full pb-[30px]" data-testid={name}>
    <Typography variant="Body" fontFamily="DM" weight="700" text={text} className="text-left w-full" />
    {ANSWERS.map((answer) => (
      <RadioButton
        key={answer.value}
        variant="Radio"
        size="S"
        label={answer.label}
        value={answer.value}
        checked={value === answer.value}
        onChange={() => onChange(answer.value)}
      />
    ))}
  </div>
);

export default function ClientConsents() {
  const { leadId, token } = useContext(GetlifeContext);
  const { getStatus } = useContext(StatusContext);
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { isMobile } = useDevice();
  const [hasNamedBeneficiaries, setHasNamedBeneficiaries] = useState<boolean | null>(null);
  const [marketing, setMarketing] = useState<Answer>("");
  const [communications, setCommunications] = useState<Answer>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    new HttpBeneficiariesRepository(leadId, token)
      .getAllBeneficiaries()
      .then((r) => {
        if (active) setHasNamedBeneficiaries((r?.beneficiaries ?? []).length > 0);
      })
      // Sin la lista se pregunta igual: con herederos legales no hay a quién avisar
      // y ms-agl no toca ninguna fila.
      .catch(() => active && setHasNamedBeneficiaries(true));
    return () => {
      active = false;
    };
  }, [leadId, token]);

  const ready =
    hasNamedBeneficiaries !== null &&
    marketing !== "" &&
    (!hasNamedBeneficiaries || communications !== "");

  const save = async () => {
    setSaving(true);
    setError(false);
    try {
      await new HttpClientConsentsRepository(leadId, token).recordClientConsents({
        marketing: "yes" === marketing,
        beneficiaryCommunications: true === hasNamedBeneficiaries && "yes" === communications,
      });
      getStatus();
    } catch (error) {
      trackSubmissionError(error);
      setError(true);
      setSaving(false);
    }
  };

  return (
    <div className="questions-outerContainer">
      <div className="questions-container">
        <div className="questions-innerContainer">
          <div className="formBuilder-container">
            <div className="flex flex-col items-center w-full pb-[30px]">
              <div className="flex flex-row items-center pb-[16px] w-full">
                <Typography
                  text={CLIENT_CONSENTS_TITLE}
                  variant="H3"
                  fontFamily="DM"
                  weight="700"
                  className="text-left w-full text-[22px]"
                />
              </div>
              <Typography
                text={CLIENT_CONSENTS_BODY}
                variant="SpanMedium"
                fontFamily="DM"
                weight="400"
                className="w-full text-left opacity-50 font-help-text-gray text-input-help-text"
              />
            </div>
            <div className="formPhysicalPerson-container">
              <div className="formPhysicalPerson-GridContainer">
                <Question
                  name="marketing_consent"
                  text={CLIENT_CONSENTS_MARKETING}
                  value={marketing}
                  onChange={setMarketing}
                />
                {hasNamedBeneficiaries ? (
                  <Question
                    name="beneficiary_communications_consent"
                    text={translate("beneficiaries.questions.allowCommunication")}
                    value={communications}
                    onChange={setCommunications}
                  />
                ) : null}
                {error ? (
                  <p role="alert" className="w-full text-left text-[#B42318] pb-[16px]">
                    {CLIENT_CONSENTS_ERROR}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className={isMobile ? "mobileShow" : "buttonsContainer"}>
          <div
            className={
              isMobile
                ? "formBuilder-buttonContainer-mobile formBuilder-buttonContainer-mobile-primary"
                : "formBuilder-buttonContainer"
            }
          >
            <ButtonDS
              label={translate("continuar")}
              onClick={save}
              disabled={!ready || saving}
              className="w-full"
              rightIcon="ArrowRightIcon"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
